import Button from '../components/Button'
import CtaBand from '../components/CtaBand'
import FaqSection from '../components/sections/FaqSection'
import Icon from '../components/Icon'
import PageHero from '../components/PageHero'
import Reveal from '../components/Reveal'
import { Section, SectionHead } from '../components/Section'
import { serviceListJsonLd, useJsonLd, useSeo } from '../lib/seo'

/**
 * The three packages.
 *
 * Feature lists are verbatim. The old site named no prices, so neither do we:
 * every tier is "Auf Anfrage" and `priceNote` says what it depends on.
 * `inherits` drives the "Alles aus ..., plus" label, so each tier lists only what
 * it adds.
 */
const PACKAGES = [
  {
    id: 'basic',
    name: 'Basic',
    tagline:
      'Die kaufmännische Grundlage: Betriebskosten sauber verwaltet und nachvollziehbar abgerechnet.',
    price: 'Auf Anfrage',
    priceNote: 'Abhängig von Objektgröße und Einheitenzahl.',
    featured: false,
    features: ['Betriebskostenverwaltung', 'Transparente Jahresabrechnung'],
  },
  {
    id: 'profi',
    name: 'Profi-Paket',
    tagline:
      'Die laufende Verwaltung Ihres Objekts - inklusive Vermietung und Betreuung von Mietern.',
    price: 'Auf Anfrage',
    priceNote: 'Unser am häufigsten gewähltes Paket.',
    featured: true,
    flag: 'Empfohlen',
    inherits: 'Basic',
    features: [
      'Umfassende Betreuung',
      'Ableseservice',
      'Finanzielle Stabilität',
      'Kosteneffizienz',
      'Mieterzufriedenheit',
      'Neuvermietung im fliegenden Wechsel',
    ],
  },
  {
    id: 'profi-plus',
    name: 'Profi-Paket-Plus',
    tagline:
      'Volle Verwaltung plus Wertsteigerung: rechtliche Beratung, Analyse und Handwerkerkoordination.',
    price: 'Auf Anfrage',
    priceNote: 'Für Eigentümer, die ihr Objekt strategisch entwickeln wollen.',
    featured: false,
    inherits: 'Profi-Paket',
    features: [
      'Rechtliche Beratung',
      'Statistische Analyse',
      'Strategische Wertsteigerungsplanung',
      'Nachhaltige Mieterhöhung',
      'Sondervermietung',
      'Kleinarbeiten inklusive',
      'Reparaturmanagement',
      'Hausmeistermanagement',
    ],
  },
]

/** What every package includes, regardless of tier. */
const ALWAYS_INCLUDED = [
  {
    icon: 'clock',
    title: 'Antwort in einem Werktag',
    text: 'Ihr Anliegen wird innerhalb eines Werktages bearbeitet - für Sie und für Ihre Mieter.',
  },
  {
    icon: 'grid',
    title: 'Zugang zur CASA App',
    text: 'Schadensmeldung mit Foto, Dokumente und digitales schwarzes Brett - für alle Beteiligten.',
  },
  {
    icon: 'pin',
    title: 'Regelmäßige Begehungen',
    text: 'Kurze Wege und örtliche Nähe verschaffen uns den Einblick in das Geschehen vor Ort.',
  },
]

/*
 * The three packages as Service entities.
 *
 * Deliberately no `offers.price`: every package is priced "Auf Anfrage", and
 * schema.org has no way to say that - a price of 0, or the string itself in a
 * numeric field, is an invalid offer and Google flags it. Naming the services
 * without a price is accurate and still lets the entity be understood.
 */
const packagesJsonLd = serviceListJsonLd(PACKAGES, '/leistungen')

export default function Leistungen() {
  useSeo({
    title: 'Leistungen',
    description:
      'Unsere Leistungen im Überblick: Basic, Profi-Paket und Profi-Paket-Plus - von der Betriebskostenverwaltung bis zur strategischen Wertsteigerungsplanung.',
    path: '/leistungen',
  })
  useJsonLd(packagesJsonLd, 'packages')

  return (
    <>
      <PageHero
        eyebrow="Leistungen"
        title="Unsere Leistungen im Überblick"
        lead="Drei Pakete, die mit Ihrem Objekt mitwachsen. Jedes Paket enthält alles aus dem vorherigen - Sie zahlen nie doppelt für dieselbe Leistung."
        breadcrumb={[{ label: 'Leistungen' }]}
      />

      <Section>
        <Reveal group className="packages">
          {PACKAGES.map((tier) => (
            <article
              className={`package${tier.featured ? ' package--featured' : ''}`}
              key={tier.id}
            >
              {tier.flag ? (
                <span className="badge package__flag">{tier.flag}</span>
              ) : null}

              <h2 className="package__name">{tier.name}</h2>
              <p className="package__tagline">{tier.tagline}</p>

              <div className="package__price">
                <p className="package__price-value">{tier.price}</p>
                <p className="package__price-note">{tier.priceNote}</p>
              </div>

              <div className="package__features">
                <p className="package__features-title">
                  {tier.inherits
                    ? `Alles aus ${tier.inherits}, plus`
                    : 'Enthaltene Leistungen'}
                </p>
                <ul className="check-list">
                  {tier.features.map((feature) => (
                    <li key={feature}>
                      <Icon name="check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="package__cta">
                <Button
                  to="/kontakt"
                  variant={tier.featured ? 'primary' : 'outline'}
                  icon="arrowRight"
                >
                  Jetzt anfragen
                </Button>
              </div>
            </article>
          ))}
        </Reveal>

        <Reveal
          className="card card--paper"
          style={{ marginTop: 'var(--s-7)', display: 'grid', gap: 'var(--s-4)' }}
        >
          <p className="eyebrow">Zur Preisgestaltung</p>
          <p style={{ margin: 0, maxWidth: '58rem' }}>
            Wir nennen bewusst keine Pauschalpreise: der Aufwand hängt an
            Einheitenzahl, Objektzustand und Verwaltungsart. Im kostenlosen
            Erstgespräch sehen wir uns Ihr Objekt an und Sie erhalten ein
            konkretes, schriftliches Angebot - ohne Verpflichtung.
          </p>
        </Reveal>
      </Section>

      <Section tone="dark">
        <div className="blueprint" aria-hidden="true" />
        <SectionHead
          eyebrow="Immer inklusive"
          title="Was in jedem Paket enthalten ist"
          lead="Unabhängig vom gewählten Umfang gelten diese Zusagen für jedes Objekt, das wir betreuen."
        />
        <Reveal group className="grid grid--3">
          {ALWAYS_INCLUDED.map((item) => (
            <article className="card card--dark feature" key={item.title}>
              <span className="icon-tile icon-tile--dark">
                <Icon name={item.icon} />
              </span>
              <h3 className="feature__title">{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </Reveal>
      </Section>

      <FaqSection />
      <CtaBand />
    </>
  )
}
